/**
 * Canvas rendering utility functions
 * These help optimize canvas rendering to prevent flickering
 */

import { Canvas } from "fabric";

// Track when the last render occurred to avoid too many renders
let lastRenderTime = 0;
const MIN_RENDER_INTERVAL = 20; // milliseconds

/**
 * Debounced renderAll function to prevent excessive canvas redraws
 * This reduces flickering by throttling render calls
 */
export const debouncedRender = (canvas: Canvas | null): void => {
  if (!canvas) return;
  
  const now = Date.now();
  if (now - lastRenderTime < MIN_RENDER_INTERVAL) {
    // If we rendered too recently, schedule a render for later
    setTimeout(() => {
      canvas.renderAll();
      lastRenderTime = Date.now();
    }, MIN_RENDER_INTERVAL);
    return;
  }
  
  // Otherwise, render immediately
  canvas.renderAll();
  lastRenderTime = now;
};

/**
 * Update object property and render if needed
 * This allows batch updates without needing to render after each one
 */
export const updateObjectAndRender = (
  canvas: Canvas | null, 
  object: any, 
  properties: Record<string, any>,
  render: boolean = true
): void => {
  if (!canvas || !object) return;
  
  // Update object properties
  object.set(properties);
  
  // Only render if explicitly requested
  if (render) {
    debouncedRender(canvas);
  }
};

/**
 * Batch update multiple objects in a single render cycle
 */
export const batchUpdate = (
  canvas: Canvas | null,
  updates: Array<{object: any, properties: Record<string, any>}>
): void => {
  if (!canvas) return;
  
  // Apply all updates
  updates.forEach(({ object, properties }) => {
    if (object) {
      object.set(properties);
    }
  });
  
  // Render only once after all updates
  debouncedRender(canvas);
};
